import { types, Instance, flow, getEnv, getRoot } from 'mobx-state-tree';
import { set, toJS } from 'mobx';
import {
  CustomerBillingAddressModel,
  CustomerPaymentProfileModel,
  CustomerShippingAddressModel,
  ICustomerShippingAddressModel,
  ICustomerBillingAddressModel,
} from '../sub-stores/Customer';
import {
  CustomerBillingAddressContract,
  CustomerCreateContract,
  CustomerDataContract,
  CustomerPaymentProfileContract,
  CustomerShippingAddressContract,
} from '../../../types/Customer';
import { RootServices } from '../../../types/RootStore';
import { AddressContract } from '../../../types/Address';
import { CartPaymentProfileContract } from '../../../types/PaymentProfile';
import { CartUpdateContract } from '../../../types/Cart';
import { CustomerLoginFormData } from '../../../components/input/CustomerLoginForm';
import { SpreedlyValues } from '../../../utils/constants';
import { ICartModel } from '../Cart';
import { CheckoutEvent } from '../../machines/checkout-machine';
import { IConfiguration } from '../Configuration';

const preferredPredicate = (
  savedItem:
    | CustomerShippingAddressContract
    | CustomerBillingAddressContract
    | CustomerPaymentProfileContract
): boolean => !!savedItem.preferred;

export const CustomerModel = types
  .model({
    id: types.optional(types.maybeNull(types.number), null),
    email: types.optional(types.maybeNull(types.string), null),
    firstName: types.optional(types.maybeNull(types.string), null),
    lastName: types.optional(types.maybeNull(types.string), null),
    isAnonymous: types.optional(types.maybeNull(types.boolean), true),
    storeCreditAvailable: types.optional(types.maybeNull(types.number), 0),
    shippingAddresses: types.array(CustomerShippingAddressModel),
    billingAddresses: types.array(CustomerBillingAddressModel),
    paymentProfiles: types.array(CustomerPaymentProfileModel),
    emailOptIn: types.optional(types.boolean, false),
    password: types.optional(types.maybeNull(types.string), null),
  })
  .volatile(() => ({
    isLoading: false,
    selectedPaymentProfileId: -1,
  }))
  .views(self => ({
    get isLoggedIn(): boolean {
      return !!self.id;
    },
    get customerData(): CustomerDataContract {
      return {
        id: self.id,
        email: self.email ?? '',
        firstName: self.firstName ?? '',
        lastName: self.lastName ?? '',
        storeCreditAvailable: self.storeCreditAvailable ?? 0,
        isAnonymous: self.isAnonymous ?? false,
      };
    },
    get newCustomerData(): CustomerCreateContract {
      return {
        email: self.email ?? '',
        firstName: self.firstName ?? '',
        lastName: self.lastName ?? '',
        password: self.password,
        anonymous: self.isAnonymous ?? false,
        emailOptIn: self.emailOptIn,
      };
    },
    get shippingAddressCount(): number {
      return self.shippingAddresses.length;
    },
    get billingAddressCount(): number {
      return self.billingAddresses.length;
    },
    get preferredShippingAddress(): CustomerShippingAddressContract | null {
      if (self.shippingAddresses.length) {
        const preferredAddress =
          self.shippingAddresses.find(preferredPredicate);
        return preferredAddress
          ? toJS(preferredAddress)
          : toJS(self.shippingAddresses[0]);
      }
      return null;
    },
    get preferredBillingAddress(): CustomerBillingAddressContract | null {
      if (self.billingAddresses.length) {
        const preferredAddress = self.billingAddresses.find(preferredPredicate);
        return preferredAddress
          ? toJS(preferredAddress)
          : toJS(self.billingAddresses[0]);
      }
      return null;
    },
    get preferredPaymentProfile(): CustomerPaymentProfileContract | null {
      if (self.paymentProfiles.length) {
        const preferredProfile = self.paymentProfiles.find(preferredPredicate);
        return preferredProfile
          ? toJS(preferredProfile)
          : toJS(self.paymentProfiles[0]);
      }
      return null;
    },
    get paymentProfileList(): CustomerPaymentProfileContract[] {
      return toJS(self.paymentProfiles);
    },
    get canUseOneClick(): boolean {
      return (
        !!self.id &&
        !!self.shippingAddresses.length &&
        !!self.billingAddresses.length &&
        !!self.paymentProfiles.length
      );
    },
    getShippingAddressById(
      addressId: number
    ): ICustomerShippingAddressModel | undefined {
      return (self.shippingAddresses || []).find(
        address => address.id === addressId
      );
    },
    getBillingAddressById(
      addressId: number
    ): ICustomerBillingAddressModel | undefined {
      return (self.billingAddresses || []).find(
        address => address.id === addressId
      );
    },
    get filteredPaymentProfiles(): CustomerPaymentProfileContract[] {
      return this.paymentProfileList.filter(
        profile => profile.id !== self.selectedPaymentProfileId
      );
    },
  }))
  .actions(self => {
    const { customerService } = getEnv<RootServices>(self);
    // Something about passing a type to `getRoot` in this spot causes TS to
    // lose the ability to infer the type of `RootModel`, so we dodge it thusly.
    const {
      cart,
      send,
      configuration,
    }: {
      cart: ICartModel;
      send: (event: CheckoutEvent) => void;
      configuration: IConfiguration;
    } = getRoot(self);
    const setIsLoading = (value: boolean): void => {
      set(self, { isLoading: value });
    };
    const setSelectedPaymentProfileId = (id = -1): void => {
      set(self, { selectedPaymentProfileId: id });
    };
    const setPassword = (value: string | null): void => {
      set(self, { password: value });
    };

    const updateIsAnonymous = (isAnonymous: boolean): void => {
      set(self, { isAnonymous });
    };
    const updateNames = (
      firstName: string | null,
      lastName: string | null
    ): void => {
      set(self, { firstName, lastName });
    };
    const updateEmail = (email: string | null): void => {
      set(self, { email });
    };
    const updateEmailOptIn = (emailOptIn: boolean): void => {
      set(self, { emailOptIn });
    };

    const updateCustomer = (customerData: CustomerCreateContract): void => {
      set(self, customerData);
    };

    const voidToBlank = (value: string | null | undefined): string => {
      return value ?? '';
    };

    const sanitizeAddress = (address: AddressContract) => {
      return {
        id: 0,
        firstName: voidToBlank(address.firstName),
        lastName: voidToBlank(address.lastName),
        address1: voidToBlank(address.address1),
        address2: voidToBlank(address.address2),
        city: voidToBlank(address.city),
        state: voidToBlank(address.state),
        postalCode: voidToBlank(address.postalCode),
        country: voidToBlank(address.country),
        phoneNumber: voidToBlank(address.phoneNumber),
        companyName: voidToBlank(address.companyName),
      };
    };
    const addShippingAddress = (address: AddressContract): void => {
      self.shippingAddresses.push({
        ...sanitizeAddress(address),
        residential: address.residential ?? false,
        preferred: true,
      });
    };

    const addShippingAddressToAuthenticatedCustomer = flow(function* (
      address: AddressContract
    ) {
      const newShippingAddress: CustomerShippingAddressContract = {
        ...sanitizeAddress(address),
        residential: address.residential ?? false,
        preferred: true,
      };
      const createdAddress: ICustomerShippingAddressModel =
        yield customerService.postShippingAddressDataAsync(newShippingAddress);
      self.shippingAddresses.push(createdAddress);
      return createdAddress;
    });

    const updateShippingAddressForAuthenticatedCustomer = flow(function* (
      addressId: number,
      updatedAddress: AddressContract
    ) {
      const updatedShippingAddress: CustomerShippingAddressContract = {
        ...sanitizeAddress(updatedAddress),
        residential: updatedAddress.residential ?? false,
        preferred: true,
        id: addressId,
      };
      const addressResponse = yield customerService.putShippingAddressDataAsync(
        updatedShippingAddress
      );
      const shippingAddressIndexToUpdate = self.shippingAddresses.findIndex(
        address => address.id === addressResponse.id
      );
      if (shippingAddressIndexToUpdate >= 0) {
        set(
          self.shippingAddresses,
          shippingAddressIndexToUpdate,
          addressResponse
        );
      }
    });

    const addBillingAddressToAuthenticatedCustomer = flow(function* (
      address: AddressContract
    ) {
      const newBillingAddress: CustomerBillingAddressContract = {
        ...sanitizeAddress(address),
        preferred: true,
      };
      const createdAddress = yield customerService.postBillingAddressDataAsync(
        newBillingAddress
      );
      self.billingAddresses.push(
        createdAddress as ICustomerShippingAddressModel
      );
      return createdAddress;
    });

    const addBillingAddress = (address: AddressContract): void => {
      self.billingAddresses.push({
        ...sanitizeAddress(address),
        preferred: true,
      });
    };

    const addPaymentProfile = (profile: CartPaymentProfileContract): void => {
      const spreedlyValues = configuration.features.enableSpreedly
        ? {
            cardToken: profile.card?.spreedlyPaymentToken,
            cardTokenProvider: SpreedlyValues.CARD_TOKEN_PROVIDER,
            paymentMethodFingerprint: profile.card?.paymentMethodFingerprint,
            transactionToken: voidToBlank(profile.card?.transactionToken),
            challengeResponse: voidToBlank(profile.card?.challengeResponse),
            browserInfo: voidToBlank(profile.card?.browserInfo),
          }
        : {};
      self.paymentProfiles.push({
        id: profile.id ?? 0,
        method: voidToBlank(profile.method),
        preferred: true,
        card: {
          ...spreedlyValues,
          pCIaaSId: voidToBlank(profile.card?.pCIaaSId),
          cardType: voidToBlank(profile.card?.cardType?.toString()),
          cardTypeName: voidToBlank(profile.card?.cardTypeName),
          cardNumber: voidToBlank(profile.card?.cardNumber),
          last4: voidToBlank(profile.card?.last4),
          holdersName: voidToBlank(profile.card?.holdersName),
          expMonth: voidToBlank(profile.card?.expMonth),
          expYear: voidToBlank(profile.card?.expYear),
          id: null,
          issueMonth: '',
          issueYear: '',
          issueNumber: '',
        },
      });
    };

    const clearAddressAndPaymentLists = (): void => {
      self.shippingAddresses.clear();
      self.billingAddresses.clear();
      self.paymentProfiles.clear();
    };
    const resetToGuestDefaults = (): void => {
      set(self, {
        id: null,
        firstName: null,
        lastName: null,
        email: null,
        isAnonymous: true,
        storeCreditAvailable: 0,
        emailOptIn: false,
        password: null,
      });
      clearAddressAndPaymentLists();
    };

    const setNewCustomerData = (cartData: CartUpdateContract): void => {
      const { billingAddress, shippingAddress, paymentProfile, customer } =
        cartData;
      updateNames(billingAddress.firstName, billingAddress.lastName);
      updateEmail(customer.emailAddress);
      updateEmailOptIn(customer.emailOptIn ?? false);
      updateIsAnonymous(!self.password || self.password === '');
      clearAddressAndPaymentLists();
      addShippingAddress(shippingAddress);
      addBillingAddress(billingAddress);
      addPaymentProfile(paymentProfile);
    };

    const saveNewCustomer = async (): Promise<number> => {
      return customerService.postNewCustomerDataAsync(self.newCustomerData);
    };

    const buildBasicCardProfile = (
      paymentProfile: CartPaymentProfileContract
    ): CustomerPaymentProfileContract => {
      const {
        issueYear,
        cardNumber,
        cardType,
        cardTypeName,
        id,
        issueNumber,
        issueMonth,
        expMonth,
        last4,
        expYear,
        holdersName,
        pCIaaSId,
      } = paymentProfile.card ?? {};

      return {
        id: paymentProfile.id,
        method: paymentProfile.method ?? '',
        preferred: true,
        card: {
          id: id ?? null,
          issueNumber: issueNumber ?? '',
          holdersName: holdersName ?? '',
          cardNumber: cardNumber ?? '',
          cardToken: null,
          cardTokenProvider: null,
          paymentMethodFingerprint: null,
          transactionToken: null,
          challengeResponse: null,
          browserInfo: configuration.payments.isSpreedly3DSEnabled
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
          issueMonth: issueMonth ?? '',
          expMonth: expMonth ?? '',
          last4: last4 ?? '',
          expYear: expYear ?? '',
          cardType: cardType ?? '',
          cardTypeName: cardTypeName ?? '',
          issueYear: issueYear ?? '',
          pCIaaSId: pCIaaSId ?? '',
        },
      };
    };

    const buildSpreedlyProfile = (
      paymentProfile: CartPaymentProfileContract
    ): CustomerPaymentProfileContract | null => {
      const {
        issueYear,
        cardType,
        cardTypeName,
        id,
        issueNumber,
        issueMonth,
        expMonth,
        last4,
        expYear,
        holdersName,
        cardToken,
        cardTokenProvider,
        paymentMethodFingerprint,
        transactionToken,
        challengeResponse,
      } = paymentProfile.card ?? {};
      let { spreedlyPaymentToken } = paymentProfile.card ?? {};
      // The spreedly token could come in as `cardToken`, so we check for that
      // while verifying that it is in fact a spreedly card.
      spreedlyPaymentToken =
        spreedlyPaymentToken ||
        (cardTokenProvider === SpreedlyValues.CARD_TOKEN_PROVIDER
          ? cardToken
          : null);

      const useSpreedly =
        configuration.features.enableSpreedly && spreedlyPaymentToken;

      if (!useSpreedly) {
        return null;
      }
      return {
        id: paymentProfile.id,
        method: paymentProfile.method ?? '',
        preferred: true,
        card: {
          id: id ?? null,
          issueNumber: issueNumber ?? '',
          holdersName: holdersName ?? '',
          cardToken: spreedlyPaymentToken ?? null,
          cardTokenProvider: SpreedlyValues.CARD_TOKEN_PROVIDER,
          paymentMethodFingerprint: paymentMethodFingerprint ?? '',
          transactionToken: transactionToken ?? null,
          challengeResponse: challengeResponse ?? null,
          browserInfo: configuration.payments.isSpreedly3DSEnabled
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
          issueMonth: issueMonth ?? '',
          expMonth: expMonth ?? '',
          last4: last4 ?? '',
          cardNumber: last4 ? `************${last4}` : '',
          expYear: expYear ?? '',
          cardType: cardType ?? '',
          cardTypeName: cardTypeName ?? '',
          issueYear: issueYear ?? '',
          pCIaaSId: '',
        },
      };
    };

    const buildStripeProfile = (
      paymentProfile: CartPaymentProfileContract
    ): CustomerPaymentProfileContract | null => {
      const {
        cardBrand,
        cardToken,
        cardTokenProvider,
        cardType,
        cardTypeName,
        challengeResponse,
        expMonth,
        expYear,
        holdersName,
        id,
        issueMonth,
        issueNumber,
        issueYear,
        last4,
        paymentMethodFingerprint,
        transactionToken,
      } = paymentProfile.card ?? {};
      if (cardTokenProvider !== 'STRIPE') {
        return null;
      }

      return {
        id: paymentProfile.id,
        method: paymentProfile.method ?? '',
        preferred: true,
        card: {
          id: id ?? null,
          issueNumber: issueNumber ?? '',
          holdersName: holdersName ?? '',
          cardBrand: cardBrand ?? null,
          cardToken: cardToken ?? null,
          cardTokenProvider: 'STRIPE',
          paymentMethodFingerprint: paymentMethodFingerprint ?? '',
          transactionToken,
          challengeResponse: challengeResponse ?? '',
          browserInfo: configuration.payments.isSpreedly3DSEnabled
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
          issueMonth: issueMonth ?? '',
          expMonth: expMonth ?? '',
          last4: last4 ?? '',
          cardNumber: `************${last4}`,
          expYear: expYear ?? '',
          cardType: cardType ?? '',
          cardTypeName: cardTypeName ?? '',
          issueYear: issueYear ?? '',
          pCIaaSId: '',
        },
      };
    };

    const saveNewPaymentProfile = async (
      paymentProfile: CartPaymentProfileContract
    ): Promise<void> => {
      const basicProfile = buildBasicCardProfile(paymentProfile);
      const spreedlyProfile = buildSpreedlyProfile(paymentProfile);
      const stripeProfile = buildStripeProfile(paymentProfile);

      // If we don't have a valid Stripe or Spreedly profile, or a
      // valid pCIaaSId, then we bail.
      if (configuration.features.enableSpreedly && !spreedlyProfile) {
        return;
      }
      if (!stripeProfile && !spreedlyProfile && !basicProfile.card.pCIaaSId) {
        return;
      }

      if (paymentProfile.card && paymentProfile.method) {
        let customerPaymentProfile: CustomerPaymentProfileContract;
        if (stripeProfile) {
          customerPaymentProfile = stripeProfile;
        } else if (spreedlyProfile) {
          customerPaymentProfile = spreedlyProfile;
        } else {
          customerPaymentProfile = basicProfile;
        }

        if (paymentProfile.id === -1 || paymentProfile.id === 0) {
          await customerService.postPaymentProfileDataAsync(
            customerPaymentProfile
          );
          return;
        }
        await customerService.putPaymentProfileDataAsync(
          customerPaymentProfile
        );
      }
    };

    const saveCustomerAddresses = async (): Promise<void> => {
      await Promise.all([
        customerService.postShippingAddressDataAsync(self.shippingAddresses[0]),
        customerService.postBillingAddressDataAsync(self.billingAddresses[0]),
      ]);
    };

    const getCustomerFlow = flow(function* () {
      try {
        const result = yield customerService.getCustomerDataAsync();
        if (result.isAnonymous) {
          return;
        }
        set(self, result);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        resetToGuestDefaults();
        return;
      }
      try {
        const [shippingAddresses, billingAddresses, paymentProfiles] =
          yield Promise.all([
            customerService.getShippingAddressListAsync(),
            customerService.getBillingAddressListAsync(),
            customerService.getPaymentProfileListAsync(),
          ]);
        set(self, { shippingAddresses, billingAddresses, paymentProfiles });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        set(self, {
          shippingAddresses: [],
          billingAddresses: [],
          paymentProfiles: [],
        });
      }
    });

    const loginFlow = flow(function* (loginData: CustomerLoginFormData) {
      yield customerService.loginCustomerAsync(loginData);
      yield configuration.getEnabledPaymentMethodsFlow();
    });

    const logoutFlow = flow(function* () {
      setIsLoading(true);
      yield customerService.logoutCustomerAsync();
      resetToGuestDefaults();
      yield configuration.getEnabledPaymentMethodsFlow();
      yield cart.updateCartFlow();
      setIsLoading(false);
      send({ type: 'USER_LOGOUT' });
    });

    return {
      addBillingAddressToAuthenticatedCustomer,
      addShippingAddressToAuthenticatedCustomer,
      getCustomerFlow,
      loginFlow,
      logoutFlow,
      setNewCustomerData,
      updateCustomer,
      saveNewPaymentProfile,
      setPassword,
      setSelectedPaymentProfileId,
      setIsLoading,
      saveNewCustomer,
      saveCustomerAddresses,
      resetToGuestDefaults,
      updateNames,
      updateEmail,
      updateEmailOptIn,
      updateIsAnonymous,
      updateShippingAddressForAuthenticatedCustomer,
    };
  });

export interface ICustomerModel extends Instance<typeof CustomerModel> {}
